import { PushNotificationSchema } from "@capacitor/push-notifications";

import { isAndroidPlatform, isIosPlatform } from "@/utils/platform.helpers";

import { AndroidPushNotificationActionPerformedData } from "../AndroidPushNotificationAdapter/AndroidPushNotificationAdapter";
import { IOSPushNotificationActionPerformedData } from "../IOSPushNotificationAdapter/IOSPushNotificationAdapter";

/**
 * Extracts the deep link URL from push notification data.
 * Handles platform-specific data structures:
 * - Android: notification.data.dataUrl
 * - iOS: notification.data.aps.alert.dataUrl
 *
 * @param notification - The push notification schema
 * @returns The extracted URL, or undefined if not found
 */
export const extractNotificationUrl = (
  notification: PushNotificationSchema,
): string | undefined => {
  if (!notification.data) {
    return undefined;
  }

  // Android: data.dataUrl
  if (isAndroidPlatform()) {
    const androidData = notification.data as AndroidPushNotificationActionPerformedData["data"];
    return androidData?.dataUrl;
  }

  // iOS: data.aps.alert.dataUrl
  if (isIosPlatform()) {
    const iosData = notification.data as IOSPushNotificationActionPerformedData["data"];
    return iosData?.aps?.alert?.dataUrl;
  }

  // Fallback: try to extract from generic data structure
  const data = notification.data as Record<string, unknown>;
  if (data.dataUrl) {
    return data.dataUrl as string;
  }
  if (data.aps) {
    const aps = data.aps as { alert?: { dataUrl?: string } };
    return aps?.alert?.dataUrl;
  }

  return undefined;
};
